import { getBffRequest } from "./base/api";
import { getApiToken } from "../helpers";
import { ClaimsBFFControllerClient } from "./protos/Claims-bffServiceClientPb";
import {
	ClaimReservation,
	CreateClaimReservationRequest,
	CreateClaimReservationResponse,
	RemoveClaimReservationRequest,
	RemoveClaimReservationResponse,
	RecoverClaimReservationRequest,
	RecoverClaimReservationResponse,
	ListClaimReservationsRequest,
	ListClaimReservationsResponse,
	GetClaimReservationRequest,
	GetClaimReservationResponse,
	GetClaimReservationFormDataRequest,
	GetClaimReservationFormDataResponse,
	ClaimsStatus
} from "./protos/messages/claims.message_pb";

export interface ListReservationsParams {
	page: number;
	limit: number;
	sortField?: string;
	sortDir?: string;
	filterUserId?: number;
	filterPassGroupId?: string;
	includeDeleted?: boolean;
}

/**
 * List claim reservations with pagination and filtering
 * NOTE: Backend only returns reservations for Claims Analysts
 */
export const listClaimReservations = async (params: ListReservationsParams): Promise<ListClaimReservationsResponse> => {
	const token: string = getApiToken();

	const request = new ListClaimReservationsRequest();
	request.setPage(params.page);
	request.setLimit(params.limit);

	if (params.sortField) {
		request.setSortField(params.sortField);
	}
	if (params.sortDir) {
		request.setSortDir(params.sortDir);
	}
	if (params.filterUserId !== undefined) {
		request.setFilterUserId(params.filterUserId);
	}
	if (params.filterPassGroupId) {
		request.setFilterPassGroupId(params.filterPassGroupId);
	}
	if (params.includeDeleted !== undefined) {
		request.setIncludeDeleted(params.includeDeleted);
	}

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.listClaimReservations(request, {})
		.then((response: ListClaimReservationsResponse) => response)
		.catch((err) => {
			console.error("Error listing claim reservations:", err);
			throw new Error(err.message || "An error occurred while listing reservations");
		});
};

/**
 * Get a single claim reservation by ID
 */
export const getClaimReservation = async (id: number): Promise<GetClaimReservationResponse> => {
	const token: string = getApiToken();

	const request = new GetClaimReservationRequest();
	request.setId(id);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getClaimReservation(request, {})
		.then((response: GetClaimReservationResponse) => response)
		.catch((err) => {
			console.error("Error getting claim reservation:", err);
			throw new Error(err.message || "An error occurred while getting reservation");
		});
};

/**
 * Create a new claim reservation
 */
export const createClaimReservation = async (
	userId: number,
	passGroupId?: string,
	benefitCode?: string,
	legalFilingState?: string,
	claimStatus?: ClaimsStatus
): Promise<CreateClaimReservationResponse> => {
	const token: string = getApiToken();

	const request = new CreateClaimReservationRequest();
	request.setUserId(userId);

	if (passGroupId) {
		request.setPassGroupId(passGroupId);
	}
	if (benefitCode) {
		request.setBenefitCode(benefitCode);
	}
	if (legalFilingState) {
		request.setLegalFilingState(legalFilingState);
	}
	if (claimStatus !== undefined) {
		request.setClaimStatus(claimStatus);
	}

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.createClaimReservation(request, {})
		.then((response: CreateClaimReservationResponse) => response)
		.catch((err) => {
			console.error("Error creating claim reservation:", err);
			throw new Error(err.message || "An error occurred while creating reservation");
		});
};

/**
 * Remove (soft delete) a claim reservation
 */
export const removeClaimReservation = async (id: number): Promise<RemoveClaimReservationResponse> => {
	const token: string = getApiToken();

	const request = new RemoveClaimReservationRequest();
	request.setId(id);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.removeClaimReservation(request, {})
		.then((response: RemoveClaimReservationResponse) => response)
		.catch((err) => {
			console.error("Error removing claim reservation:", err);
			throw new Error(err.message || "An error occurred while removing reservation");
		});
};

/**
 * Recover a soft-deleted claim reservation
 */
export const recoverClaimReservation = async (id: number): Promise<RecoverClaimReservationResponse> => {
	const token: string = getApiToken();

	const request = new RecoverClaimReservationRequest();
	request.setId(id);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.recoverClaimReservation(request, {})
		.then((response: RecoverClaimReservationResponse) => response)
		.catch((err) => {
			console.error("Error recovering claim reservation:", err);
			throw new Error(err.message || "An error occurred while recovering reservation");
		});
};

/**
 * Get form data (dropdowns) for creating a claim reservation
 */
export const getClaimReservationFormData = async (): Promise<GetClaimReservationFormDataResponse> => {
	const token: string = getApiToken();

	const request = new GetClaimReservationFormDataRequest();

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getClaimReservationFormData(request, {})
		.then((response: GetClaimReservationFormDataResponse) => response)
		.catch((err) => {
			console.error("Error getting claim reservation form data:", err);
			throw new Error(err.message || "An error occurred while loading form data");
		});
};

// Export proto types for convenience
export type { ClaimReservation };
